import React, { useEffect, useState, useRef } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import defer from 'lodash/defer';
import {
  Button,
  Icon,
  focusTrap,
  ProfilePhoto,
  TranslationProvider,
  utils,
} from '@resy/ui-kitchen';
import { loadResyConfigThunk } from '../../../../../../../ui-kitchen/src/reducers';
import { IDLE } from '../../../../../../../ui-kitchen/src/constants/asyncLoadingStatuses';
import ResyCalendar from '../../../../../../../ui-kitchen/src/components/ResyCalendar';
import {
  setSelectedCoreISODate,
  setSelectedCoreSeats,
  setSelectedCoreTime,
} from '../../../../../../../ui-kitchen/src/reducers/resyCoreSlice';
import { ISO_DATE_FORMAT, ALL_DAY } from '../../../../../../../ui-kitchen/src/constants/resyCore';
import Selector from '../../../Nav/Selector';
import Menu from '../../../Menu';
import menuItemsConfig, { getLoggedOutMenuItems } from '../../../../containers/MenuContainer/menuItems';
import selectIsResyRewardsEnabled from '../../../ResyRewards/selectors';
import './AmexHeaderLogin.scss';
import Close from '../../../../svg/dynamic/close.svg';
import {
  getUserThunk, selectIsConcierge, selectIsGlobalDiningAccess, userLogoutThunk,
} from '../../../../../../../ui-kitchen/src/reducers/userSlice';
import { getCustomDateRange, getCalculatedTimeOptions, handleMktAnalytics } from '../../AmexUtils';
import { CTA_EVENT_NAME, AMEX_MODAL } from '../../constants';

const AmexHeaderLogin = (
  {
    $rootScope,
    $routeParams,
    Analytics,
    isAuthenticated,
    matchMedia,
    resyHeaderConfig,
    configStatus,
    isConcierge,
    isGda,
    isGdaEnabled,
    isResyRewardsEnabled,
    profilePhotoUrl,
    selectedCoreISODate,
    selectedCoreSeats,
    selectedCoreTime,
    dispatch,
    dateLimitSelector,
    timeLimitSelector,
    isNonUS,
  }
) => {
  const [searchFields, setSearchFields] = useState({
    datepicker: {
      model: null,
      config: {
        label: 'Today',
        calendarNumber: {
          default: 1,
          lg: 2,
        },
        calendarLeadDays: null,
      },
    },
  });
  const hideSelectorsPageConfig = resyHeaderConfig?.hide_selectors || false;
  const hideSelectorsRootScope = !!$rootScope.hideSelectors;
  const hideSelectors = hideSelectorsRootScope || !!hideSelectorsPageConfig;

  const { venueInventoryUtils } = utils;
  const [isBackdropOpen, setIsBackdropOpen] = useState(false);
  const [menuItems, setMenuItems] = useState([]);
  const [dropdowns, setDropdowns] = useState({
    date: false,
    time: false,
    menu: false,
    seats: false,
  });
  const [activeMonthMomentObject, setActiveMonthMomentObject] = useState(null);
  const [inventoryByDay, setInventoryByDay] = useState(venueInventoryUtils.getDateRange(moment()));
  const [displayDate, setDisplayDate] = useState(moment(selectedCoreISODate).calendar());
  const [displayTime, setDisplayTime] = useState(selectedCoreTime);
  const [displaySeats, setDisplaySeats] = useState(selectedCoreSeats);
  const profileMenuRef = useRef();
  let futureDateTimeOptions;

  const onDropdownClickAmexMarketing = (dropdown) => {
    const clonedDropdowns = dropdowns;
    Object.keys(clonedDropdowns).forEach((key) => {
      if (key === dropdown) {
        const shouldOpen = !clonedDropdowns[key];
        clonedDropdowns[key] = shouldOpen;
        setIsBackdropOpen(shouldOpen);
        setDropdowns((prevState) => ({
          ...prevState,
          ...clonedDropdowns,
        }));
        $rootScope.isBackdropOpen = shouldOpen;

        if (shouldOpen) {
          if (dropdown === 'seats' || dropdown === 'time') {
            setIsBackdropOpen(false);
            $rootScope.isBackdropOpen = false;
          } else if (dropdown === 'menu') {
            if (matchMedia.lg) {
              focusTrap.focusModal(
                '.AmexMenuContainer__menu-and-auth-button'
              );
            }
          }
          $rootScope.$digest();
        } else {
          focusTrap.focusRemove();
          if (dropdown === 'date') {
            // the date button re-renders, need a delay before setting focus
            setTimeout(() => document.querySelector('.AmexNav button.DateSelector')?.focus(), 350);
          }
          $rootScope.$digest();
        }
      }
    });
  };

  const closeEverythingAmexmarketing = () => {
    const clonedDropdowns = dropdowns;
    Object.keys(clonedDropdowns).forEach((key) => { clonedDropdowns[key] = false; });
    setIsBackdropOpen(false);
    setDropdowns((prevState) => ({
      ...prevState,
      ...clonedDropdowns,
    }));
    focusTrap.focusRemove();
    $rootScope.isBackdropOpen = false;
    $rootScope.$digest();
  };

  const handleFocusAmexMarketing = () => {
    const calendarContainer = document.querySelector('#AmexNav__calendar-dialog');
    const activeDate = document.querySelector('#active-date');

    if (calendarContainer && activeDate) {
      activeDate.focus();
    } else if (calendarContainer) {
      calendarContainer.focus();
    }
  };

  const setMonthValuesAmexMarketing = () => {
    const coreISODate = (dateLimitSelector?.enabled)
      ? (dateLimitSelector?.start_date) : selectedCoreISODate;

    const activeDate = moment(coreISODate).isBefore(moment(), 'days') ? moment() : moment(coreISODate);

    const todaysDate = moment();
    setActiveMonthMomentObject(activeDate);

    if (dateLimitSelector?.enabled) {
      const { start_date: startDate, end_date: endDate } = dateLimitSelector;
      setInventoryByDay(getCustomDateRange(startDate, endDate));
    } else {
      setInventoryByDay(venueInventoryUtils.getDateRange(todaysDate));
    }

    defer(() => $rootScope.$digest());
  };

  const onDateSelectAmexMarketing = async (selectedDate, { usesBackdrop = true } = {}) => {
    dispatch(setSelectedCoreISODate(selectedDate.format(ISO_DATE_FORMAT)));
    setDisplayDate(moment(selectedDate.format(ISO_DATE_FORMAT)).calendar());
    setSearchFields((prevState) => ({
      ...prevState,
      datepicker: {
        config: {
          label: moment(selectedDate.format(ISO_DATE_FORMAT)).calendar(),
        },
      },
    }));
    if (usesBackdrop) {
      onDropdownClickAmexMarketing('date');
      // eslint-disable-next-line react/prop-types
    } else if (!$rootScope.$$phase) {
      $rootScope.$digest();
    } else {
      setTimeout(() => $rootScope.$digest(), 50);
    }
  };

  const onPartySizeChange = async ({ party_size: quantity }) => {
    const previousQuantity = selectedCoreSeats;
    if (!previousQuantity) return;
    if (String(previousQuantity) !== quantity) {
      dispatch(setSelectedCoreSeats(quantity));
      setDisplaySeats(quantity);
      closeEverythingAmexmarketing();
      $rootScope.$evalAsync();
    }
  };

  const onPartySizeClick = () => {
    onDropdownClickAmexMarketing('seats');
    $rootScope.$evalAsync();
  };

  const onTimeChange = async ({ time }) => {
    if (time === null) return;
    dispatch(setSelectedCoreTime(time));
    if (String(selectedCoreTime) !== time) {
      setDisplayTime(time);
      closeEverythingAmexmarketing();
      $rootScope.$evalAsync();
    }
  };

  const onTimeClick = () => {
    onDropdownClickAmexMarketing('time');
    $rootScope.$evalAsync();
  };

  const toggleCalendar = () => {
    onDropdownClickAmexMarketing('date');
    // race condition, activeDate does not exist first time it is called. timeout gives it time to render for ada requirements. can be removed when react calendar is used instead
    setTimeout(handleFocusAmexMarketing, 350);
  };

  const loadDateAmexMarketing = () => {
    const coreISODate = (dateLimitSelector?.enabled)
      ? (dateLimitSelector?.start_date) : selectedCoreISODate;
    setSearchFields((prevState) => ({
      ...prevState,
      datepicker: {
        model: moment(coreISODate, ISO_DATE_FORMAT).toDate(),
        config: {
          label: moment(coreISODate, ISO_DATE_FORMAT).calendar(),
        },
      },
    }));

    setDisplayDate(moment(coreISODate).calendar());
    dispatch(setSelectedCoreISODate(moment(coreISODate).format(ISO_DATE_FORMAT)));
    const activeDate = moment(coreISODate).isBefore(moment(), 'days') ? moment() : moment(coreISODate);
    setActiveMonthMomentObject(activeDate);
  };

  const generateMenuItems = async () => {
    let clonedMenuItems = [];

    if (!matchMedia.lg) {
      const loggedOutMenuItems = getLoggedOutMenuItems({ isGdaEnabled, isResyRewardsEnabled });

      if (!isAuthenticated) {
        clonedMenuItems = [...loggedOutMenuItems];
      } else {
        clonedMenuItems = [
          ...(isConcierge ? menuItemsConfig.concierge : []),
          ...menuItemsConfig.loggedIn,
          { key: 'dividerKey', name: 'divider' },
          ...loggedOutMenuItems,
        ];
      }
    } else {
      clonedMenuItems = [
        ...(isConcierge ? menuItemsConfig.concierge : []),
        ...menuItemsConfig.loggedIn,
        { key: 'dividerKey', name: 'divider' },
      ];
    }
    setMenuItems(clonedMenuItems);
  };

  const handleClickOutside = (e) => {
    if (profileMenuRef?.current?.contains(e.target)) {
      closeEverythingAmexmarketing();
    }
  };

  const toggleMenuAmexMarketing = () => {
    onDropdownClickAmexMarketing('menu');
    $rootScope.$evalAsync();
  };

  $rootScope.searchProps = {
    onDateSelectAmexMarketing,
    onPartySizeChange,
    onPartySizeClick,
    onTimeChange,
    onTimeClick,
    toggleCalendar,
    dropdowns,
    searchFields,
    selectedSeats: null,
    selectedTime: ALL_DAY,
  };

  const loadPartySizeAmexMarketing = () => {
    $rootScope.searchProps.selectedSeats = selectedCoreSeats;
    setDisplaySeats(selectedCoreSeats);
  };

  const loadTimeAmexMarketing = () => {
    const selectedDisplayTime = selectedCoreTime;

    setDisplayTime(selectedDisplayTime);
    dispatch(setSelectedCoreTime(selectedDisplayTime));
    $rootScope.searchProps.selectedTime = selectedDisplayTime;
  };

  useEffect(() => {
    // clean up controller
    if (isAuthenticated) {
      const loadUserAmexMarketing = () => dispatch(getUserThunk(true));
      loadUserAmexMarketing();
    }
    loadDateAmexMarketing();
    loadTimeAmexMarketing();
    loadPartySizeAmexMarketing();
    setMonthValuesAmexMarketing();
    generateMenuItems();
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      generateMenuItems();
      closeEverythingAmexmarketing();
    }
  }, [isAuthenticated]);

  useEffect(() => {
    generateMenuItems();
  }, [isGdaEnabled, isResyRewardsEnabled]);

  useEffect(() => {
    setMonthValuesAmexMarketing();
    setDisplayDate(moment(selectedCoreISODate).calendar());
  }, [selectedCoreISODate]);

  useEffect(() => {
    setDisplayTime(selectedCoreTime);
    $rootScope.searchProps.selectedTime = selectedCoreTime;
  }, [selectedCoreTime]);

  useEffect(() => {
    $rootScope.searchProps.selectedSeats = selectedCoreSeats;
    setDisplaySeats(selectedCoreSeats);
  }, [selectedCoreSeats]);

  useEffect(() => {
    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
  }, [configStatus]);

  const onLoginAmexMarketing = () => {
    const extraPrams = {
      action_name: 'Header Login - Click',
    };

    handleMktAnalytics(Analytics?.event, CTA_EVENT_NAME, extraPrams, () => $rootScope.$broadcast(AMEX_MODAL, 'signin'));
  };

  const onLogoutAmexMarketing = () => {
    dispatch(userLogoutThunk());
    closeEverythingAmexmarketing();
  };

  const renderSelectors = () => (
    <div className="AmexNav__selectors-container">
      <Selector
        date={displayDate}
        isMobile={false}
        onPartySizeChange={onPartySizeChange}
        onPartySizeClick={onPartySizeClick}
        onTimeChange={onTimeChange}
        onTimeClick={onTimeClick}
        showTimeFilter
        time={displayTime}
        toggleCalendar={toggleCalendar}
        quantitySelected={displaySeats}
        calendarExpanded={dropdowns.date}
        {...(timeLimitSelector?.enabled
          ? { timeOptions: getCalculatedTimeOptions(timeLimitSelector, futureDateTimeOptions) } : {})}
      />
      {dropdowns.date && (
        <div
          id="AmexNav__calendar-dialog"
          className={`AmexNav__calendar-container ${dropdowns.date ? 'AmexNav__calendar-container--open' : ''}`}
          aria-hidden={!dropdowns.date}
          role="dialog"
        >
          <div className="ResyCalendar__Dropdown ResyCalendar__Dropdown--global-nav">
            <ResyCalendar
              Analytics={Analytics}
              $routeParams={$routeParams}
              clickableElementIdsToIgnore={['DateSelector__button']}
              numberOfMonths={1}
              getAriaLabel={venueInventoryUtils.getAriaLabel}
              calendarMap={inventoryByDay}
              closeCalendar={toggleCalendar}
              currentMonth={activeMonthMomentObject}
              onClick={onDateSelectAmexMarketing}
              selectedDate={selectedCoreISODate}
              titleText={<span>Reservation Date</span>}
              venueCity="Global Nav"
            />
          </div>
        </div>
      )}
    </div>
  );

  const renderMenuAndAuthButtonForMobile = () => (
    isAuthenticated ? (
      <div
        className={`AmexMenuContainer__menu-and-auth-button
        ${dropdowns.menu ? 'AmexMenuContainer__menu-and-auth-button--open' : ''}`}
      >
        <Menu Analytics={Analytics} items={menuItems} isGda={isGda} isNonUS={isNonUS} closeMenu={closeEverythingAmexmarketing} />
        <div className="AmexMenuContainer__auth-button-container logged-in">
          <TranslationProvider translationKey="signout">
            <Button
              variant="no-style"
              onClick={onLogoutAmexMarketing}
              data-test-id="menu_container-button-sign_out"
            >
              Sign out
            </Button>
          </TranslationProvider>
        </div>
      </div>
    ) : (
      <div
        className={`AmexMenuContainer__menu-and-auth-button
          ${(dropdowns.menu || dropdowns.date) ? 'AmexMenuContainer__menu-and-auth-button--open' : ''}`}
      >
        <Menu Analytics={Analytics} items={menuItems} isGda={isGda} isNonUS={isNonUS} closeMenu={closeEverythingAmexmarketing} />
        <div className="AmexMenuContainer__auth-button-container logged-out">
          <TranslationProvider translationKey="login">
            <Button
              variant="login"
              onClick={onLoginAmexMarketing}
              data-test-id="menu_container-button-login"
            >
              Log in
            </Button>
          </TranslationProvider>
        </div>
      </div>
    )
  );

  const renderMenuAndAuthButtonForDesktop = () => (
    <div className="AmexMenuContainer__menu-and-auth-button">
      <Button
        aria-label="Close Profile menu"
        variant="close"
        className="AmexMenuContainer__close-container"
        onClick={closeEverythingAmexmarketing}
        data-test-id="menu_container-button-close"
      />
      <Menu Analytics={Analytics} items={menuItems} />
      <div className="AmexMenuContainer__auth-button-container logged-in">
        <TranslationProvider translationKey="signout">
          <Button
            variant="no-style"
            onClick={onLogoutAmexMarketing}
            data-test-id="menu_container-button-logout"
          >
            Sign out
          </Button>
        </TranslationProvider>
      </div>
    </div>
  );

  const renderMobile = () => (
    <>
      {!hideSelectors && (
        <div className="AmexNav__bottombar">
          {renderSelectors()}
        </div>
      )}
      {(dropdowns.menu || dropdowns.date) ? (
        <Button
          className="AmexNav__close-icon"
          name="close"
          variant="close"
          onClick={closeEverythingAmexmarketing}
          aria-label="Close Profile menu"
        >
          <Close />
        </Button>
      ) : (
        <>
          {isAuthenticated ? (
            <Button
              aria-expanded={dropdowns.menu}
              variant="no-style"
              className={`ResyIcon__container ${isGda ? 'ResyIcon__container--rga' : ''}`}
              onClick={toggleMenuAmexMarketing}
              data-test-id="menu_container-button-profile_photo"
              data-dd-privacy="mask"
              aria-label="Profile menu"
            >
              <ProfilePhoto
                isGda={isGda}
                url={profilePhotoUrl}
              />
            </Button>
          ) : (
            <Button
              variant="no-style"
              className="ResyIcon__container"
              onClick={toggleMenuAmexMarketing}
              data-test-id="menu_container-button-log_in"
              aria-label="Mobile menu"
            >
              <Icon name="hamburger" />
            </Button>
          )}
        </>
      )}
      {dropdowns.menu && renderMenuAndAuthButtonForMobile()}
    </>
  );

  const renderDesktop = () => (
    <>
      {!hideSelectors && renderSelectors()}
      {!isAuthenticated && (
        <TranslationProvider translationKey="login">
          <Button
            variant="login"
            onClick={onLoginAmexMarketing}
            data-test-id="menu_container-button-log_in"
          >
            Log in
          </Button>
        </TranslationProvider>
      )}
      {isAuthenticated && (
        <Button
          aria-expanded={dropdowns.menu}
          variant="no-style"
          className={`ResyIcon__container ${isGda ? 'ResyIcon__container--rga' : ''}`}
          onClick={toggleMenuAmexMarketing}
          aria-label="Profile menu"
          data-test-id="menu_container-button-profile_photo"
          data-dd-privacy="mask"
        >
          <ProfilePhoto
            isGda={isGda}
            url={profilePhotoUrl}
          />
        </Button>
      )}
      {isAuthenticated && dropdowns.menu && renderMenuAndAuthButtonForDesktop()}
    </>
  );

  const containerClasses = [
    'AmexMenuContainer',
    matchMedia.lg ? 'AmexMenuContainer--desktop' : 'AmexMenuContainer--mobile',
    isAuthenticated ? 'auth' : '',
  ].filter(Boolean).join(' ');

  return (
    <>
      <div
        className={containerClasses}
      >
        {matchMedia.lg ? renderDesktop() : renderMobile()}
      </div>
      {matchMedia.lg && (
        <div
          className={`AmexNav__backdrop ${isBackdropOpen ? 'AmexNav__backdrop--open' : ''}`}
          ref={profileMenuRef}
        />
      )}
    </>
  );
};

AmexHeaderLogin.propTypes = {
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func,
    $digest: PropTypes.func,
    $evalAsync: PropTypes.func,
    $on: PropTypes.func,
    isBackdropOpen: PropTypes.bool,
    nav: PropTypes.shape({}),
    hideSelectors: PropTypes.bool,
    searchProps: PropTypes.shape({
      selectedSeats: PropTypes.number,
      selectedTime: PropTypes.string,
      searchFields: PropTypes.shape({
        datepicker: PropTypes.shape({
          model: PropTypes.shape({}),
        }),
      }),
    }),
  }).isRequired,
  $location: PropTypes.shape({
    path: PropTypes.func,
    search: PropTypes.func.isRequired,
  }).isRequired,
  $routeParams: PropTypes.shape({}).isRequired,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  isAuthenticated: PropTypes.bool.isRequired,
  matchMedia: PropTypes.shape({
    xs: PropTypes.bool,
    sm: PropTypes.bool,
    md: PropTypes.bool,
    lg: PropTypes.bool,
    xl: PropTypes.bool,
  }).isRequired,
  resyHeaderConfig: PropTypes.shape({
    hide_selectors: PropTypes.bool,
  }),
  tagConfig: PropTypes.shape({
    resy_header: PropTypes.shape({}),
  }).isRequired,
  configStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  isConcierge: PropTypes.bool.isRequired,
  isGda: PropTypes.bool.isRequired,
  isGdaEnabled: PropTypes.bool,
  isResyRewardsEnabled: PropTypes.bool,
  profilePhotoUrl: PropTypes.string,
  selectedCoreISODate: PropTypes.string.isRequired,
  selectedCoreSeats: PropTypes.number.isRequired,
  selectedCoreTime: PropTypes.string.isRequired,
  dateLimitSelector: PropTypes.shape({
    enabled: PropTypes.bool,
    start_date: PropTypes.string,
    end_date: PropTypes.string,
  }),
  timeLimitSelector: PropTypes.shape({
    enabled: PropTypes.bool,
    start_time: PropTypes.string,
    end_time: PropTypes.string,
  }),
  isNonUS: PropTypes.bool.isRequired,
};

AmexHeaderLogin.defaultProps = {
  isGdaEnabled: false,
  isResyRewardsEnabled: false,
  profilePhotoUrl: null,
  resyHeaderConfig: {
    hide_selectors: false,
  },
  dateLimitSelector: null,
  timeLimitSelector: null,
};

const mapStateToProps = (state) => {
  const {
    resyConfig,
    resyCore: {
      selectedCoreISODate,
      selectedCoreSeats,
      selectedCoreTime,
    },
    user,
    ipdata,
  } = state;
  return ({
    configStatus: resyConfig.configStatus,
    isConcierge: selectIsConcierge(state),
    isGda: selectIsGlobalDiningAccess(state),
    isGdaEnabled: resyConfig.config?.enable_global_dining_access,
    isResyRewardsEnabled: selectIsResyRewardsEnabled(state),
    profilePhotoUrl: user.userData?.profile_image_url,
    selectedCoreISODate,
    selectedCoreSeats,
    selectedCoreTime,
    isNonUS: ipdata?.isNonUS,
  });
};

export { AmexHeaderLogin as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps)(AmexHeaderLogin);
